import useTiclickTableConfig from '@shared/hooks/use-ticlick-table-config';
import { Button } from '@src/design-system/button';
import AddIcon from '@shared/assets/icons/shared/add-icon';

import { TABLES_KEYS } from '@shared/constants/tables-keys';
import type { ActionItemProps } from '@src/design-system/molecules/ticlick-table/ticlick-table-table-actions';

import {
  Dialog,
  DialogContent,
  DialogTrigger,
} from '@src/design-system/dialog';
import { useHasPermission } from '@shared/hooks/use-has-permission';
import { entities } from '@shared/constants/entities';

import type { TLeadNote } from '@shared/types/lead-note';

import leadApi from '@features/leads/services/lead-api';
import leadNoteApi from '@features/leads/services/lead-note-api';
import { CreateLeadModal } from '../../list/dialogs/create-lead-modal';
import { staticColumns } from './static-columns';
import RowActionsCell from '../../list/lead-table/row-actions-cell';
import { VIEW_ENTITIES } from '@src/shared/types/view';
import { LeadFilters } from '@shared/features/views/components/entities-filters/leads';

export type FollowUpsTab = 'unresolved' | 'resolved';

export function useFollowUpsTable(tab: FollowUpsTab) {
  leadApi.useBulkAssignLabels();

  const hasPermission = useHasPermission();
  const canAddLead = hasPermission(entities.lead, 'add');

  const actionsTable: ActionItemProps<TLeadNote>[] = [
    {
      key: 'add',
      visibility: 'none-selection',
      minWidth: 77,
      render: () => (
        <Dialog>
          <DialogTrigger data-ticlick-table-action='add-lead'>
            <Button
              variant='primary'
              className='h-9 w-[77px] pr-5 text-sm font-medium rounded-xl'
            >
              <AddIcon />
              Add
            </Button>
          </DialogTrigger>
          <DialogContent defaultValue={'add_lead'}>
            <CreateLeadModal />
          </DialogContent>
        </Dialog>
      ),
      isDisplay: canAddLead,
    },
  ];

  const table = useTiclickTableConfig({
    options: { columns: staticColumns },
    filters: LeadFilters,
    identifyingData: {
      tableKey:
        tab === 'resolved'
          ? TABLES_KEYS.followUpsResolvedTable
          : TABLES_KEYS.followUpsUnresolvedTable,

      viewEntityType: VIEW_ENTITIES.lead,
    },
    fetchingData: {
      fetchFn: leadNoteApi.useFetch,
      query: {
        has_next_follow_up_date: true,
        is_resolved: tab === 'resolved',
      },
    },
    rowActions: [
      {
        key: 'actions',
        render: (row) => (
          <RowActionsCell
            isLeadNote
            lead={row.original.lead}
          />
        ),
        minWidth: 20,
      },
    ],
  });

  return {
    table,
    actionsTable,
  };
}
